/**
 * Mixin para detectar navegadores in-app de redes sociales
 * y forzar la apertura en el navegador predeterminado del dispositivo
 *
 * Funciona de forma completamente automática:
 * - Detecta el navegador in-app
 * - Intenta redirigir al navegador predeterminado
 * - Si no funciona, continúa normalmente sin molestar al usuario
 */
export default {
    data() {
        return {
            isInAppBrowser: false,
            inAppBrowserType: null,
            showInAppBrowserModal: false,
        };
    },

    computed: {
        /**
         * Detecta si es un dispositivo móvil
         */
        isMobileDevice() {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        },

        /**
         * Detecta si es iOS
         */
        isIOS() {
            return /iPhone|iPad|iPod/i.test(navigator.userAgent);
        },

        /**
         * Detecta si es Android
         */
        isAndroid() {
            return /Android/i.test(navigator.userAgent);
        },

        /**
         * Obtiene el nombre legible del navegador in-app
         */
        inAppBrowserName() {
            const names = {
                instagram: 'Instagram',
                facebook: 'Facebook',
                messenger: 'Messenger',
                whatsapp: 'WhatsApp',
                twitter: 'Twitter/X',
                tiktok: 'TikTok',
                snapchat: 'Snapchat',
                linkedin: 'LinkedIn',
                pinterest: 'Pinterest',
                telegram: 'Telegram',
                line: 'LINE',
                wechat: 'WeChat',
                unknown: 'esta aplicación'
            };
            return names[this.inAppBrowserType] || names.unknown;
        }
    },

    methods: {
        /**
         * Detecta si el usuario está usando un navegador in-app
         */
        detectInAppBrowser() {
            const ua = navigator.userAgent || navigator.vendor || window.opera || '';

            // Excluir bots, herramientas de auditoría (Lighthouse/PageSpeed) y rastreadores
            const isBotOrAudit = /Lighthouse|HeadlessChrome|Googlebot|bingbot|Bytespider|PageSpeed|Chrome-Lighthouse/i.test(ua);
            if (isBotOrAudit) {
                this.isInAppBrowser = false;
                this.inAppBrowserType = null;
                return false;
            }

            // Patrones de detección para diferentes navegadores in-app
            const inAppPatterns = {
                // Instagram
                instagram: /Instagram/i,
                // Facebook y Messenger
                facebook: /FBAN|FBAV|FB_IAB|FB4A|FBIOS/i,
                messenger: /Messenger/i,
                // WhatsApp
                whatsapp: /WhatsApp/i,
                // Twitter/X
                twitter: /Twitter/i,
                // TikTok
                tiktok: /TikTok|BytedanceWebview|ByteLocale/i,
                // Snapchat
                snapchat: /Snapchat/i,
                // LinkedIn
                linkedin: /LinkedIn/i,
                // Pinterest
                pinterest: /Pinterest/i,
                // Telegram
                telegram: /Telegram/i,
                // LINE
                line: /Line\//i,
                // WeChat
                wechat: /MicroMessenger/i,
            };

            // Verificar cada patrón
            for (const [browser, pattern] of Object.entries(inAppPatterns)) {
                if (pattern.test(ua)) {
                    this.isInAppBrowser = true;
                    this.inAppBrowserType = browser;
                    return true;
                }
            }

            // Detección genérica de WebView
            const isWebView = /(wv|\.0\.0\.0)/.test(ua) || 
                              (this.isAndroid && /Version\/[\d.]+/.test(ua) && !/Chrome\/[\d.]+/.test(ua));

            if (isWebView && this.isMobileDevice) {
                this.isInAppBrowser = true;
                this.inAppBrowserType = 'unknown';
                return true;
            }

            this.isInAppBrowser = false;
            this.inAppBrowserType = null;
            return false;
        },

        /**
         * Verifica si ya se intentó redirigir en esta sesión
         */
        wasRedirectAttempted() {
            return sessionStorage.getItem('inAppBrowserRedirectAttempted') === 'true';
        },

        /**
         * Marca que ya se intentó redirigir
         */
        markRedirectAttempted() {
            sessionStorage.setItem('inAppBrowserRedirectAttempted', 'true');
        },

        /**
         * Intenta abrir automáticamente en el navegador predeterminado
         * Si no funciona, simplemente continúa sin hacer nada
         */
        tryAutoOpenInBrowser() {
            const ua = navigator.userAgent || '';
            if (/Lighthouse|HeadlessChrome|Googlebot|bingbot|Bytespider|PageSpeed|Chrome-Lighthouse/i.test(ua)) {
                return;
            }

            const currentUrl = window.location.href;

            // Marcar que ya intentamos redirigir para no hacer loops
            this.markRedirectAttempted();

            if (this.isAndroid) {
                // Método 1: Intent URL para Android - abre en el navegador predeterminado
                // Este es el método más confiable para Android
                const intentUrl = `intent://${currentUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;end`;

                // Redirigir usando intent
                window.location.href = intentUrl;
                return;
            }

            if (this.isIOS) {
                // Para iOS, los métodos automáticos son muy limitados
                // debido a las restricciones de seguridad de Apple.
                // 
                // Orden de prioridad para iOS:
                // 1. Safari (navegador nativo de Apple)
                // 2. Google Chrome
                // 3. Firefox
                // 4. Microsoft Edge
                // 5. Brave
                // 6. Opera

                const urlWithoutProtocol = currentUrl.replace(/^https?:\/\//, '');
                const isHttps = currentUrl.startsWith('https://');

                // Lista de esquemas URL para diferentes navegadores en iOS
                // Safari primero ya que es el navegador nativo
                const iosBrowserSchemes = [
                    // 1. Safari - Esquema nativo de iOS para abrir en Safari
                    // Usando x-safari-https:// o intentando con el esquema de Safari
                    `x-safari-${isHttps ? 'https' : 'http'}://${urlWithoutProtocol}`,

                    // 2. Safari alternativo - Usar openURL con esquema de sistema
                    // Algunos WebViews respetan este formato
                    `safari-${isHttps ? 'https' : 'http'}://${urlWithoutProtocol}`,

                    // 3. Google Chrome iOS
                    `${isHttps ? 'googlechromes' : 'googlechrome'}://${urlWithoutProtocol}`,

                    // 4. Firefox iOS
                    `firefox://open-url?url=${encodeURIComponent(currentUrl)}`,

                    // 5. Microsoft Edge iOS
                    `microsoft-edge-${isHttps ? 'https' : 'http'}://${urlWithoutProtocol}`,

                    // 6. Brave iOS
                    `brave://open-url?url=${encodeURIComponent(currentUrl)}`,

                    // 7. Opera iOS
                    `opera-${isHttps ? 'https' : 'http'}://${urlWithoutProtocol}`,
                ];

                // Función para intentar abrir cada esquema con timeout
                const tryOpenScheme = (index) => {
                    if (index >= iosBrowserSchemes.length) {
                        // Si ningún esquema funciona, el usuario continúa
                        // navegando en el WebView de la app - no hay interrupción
                        return;
                    }

                    try {
                        const scheme = iosBrowserSchemes[index];

                        // Guardar el tiempo actual para detectar si la app se abrió
                        const startTime = Date.now();

                        // Intentar abrir el esquema
                        window.location.href = scheme;

                        // Si después de un breve tiempo seguimos aquí,
                        // significa que el esquema no funcionó, intentar el siguiente
                        setTimeout(() => {
                            // Si han pasado menos de 500ms, probablemente el esquema falló
                            if (Date.now() - startTime < 500) {
                                tryOpenScheme(index + 1);
                            }
                        }, 150);
                    } catch (e) {
                        // Si hay error, intentar el siguiente esquema
                        tryOpenScheme(index + 1);
                    }
                };

                // Iniciar con el primer esquema (Safari)
                tryOpenScheme(0);
                return;
            }

            // Si llegamos aquí, los métodos automáticos no funcionaron
            // Simplemente continuamos con el flujo normal - el usuario no nota nada
        },

        /**
         * Inicializa la detección y redirección automática
         * Todo es silencioso: si funciona, redirige; si no, continúa normal
         */
        initInAppBrowserDetection() {
            // Solo ejecutar si estamos en un navegador in-app y no hemos intentado antes
            if (this.detectInAppBrowser() && !this.wasRedirectAttempted()) {
                // Pequeño delay para asegurar que la página está lista
                setTimeout(() => {
                    this.tryAutoOpenInBrowser();
                }, 100);
            }
            // Si no es in-app browser o ya intentamos, simplemente no hacemos nada
            // El usuario continúa su experiencia normal
        }
    },

    mounted() {
        // Auto-detectar al montar el componente (opcional, puede llamarse manualmente)
        // this.initInAppBrowserDetection();
    }
};
